import type { AppType } from '@/models/enums/AppType';

// The media library. A CREATIVE is one piece of work; a RENDITION is one file of it at one shape, so a
// creative with a 10:16 and a 4:5 rendition is one ad that plays correctly on Tabletop and on Dual Display.
//
// Shared across the whole Media Network — Ad Banner and Cross-Sell draw from the same shelf, and a creative
// exists whether or not anything uses it. That is why the library is its own nav item rather than something
// you reach through a playlist.

export const MediaType = {
    Image: 0,
    Video: 1,
} as const;

export type MediaType = (typeof MediaType)[keyof typeof MediaType];

export const RenditionUploadState = {
    Pending: 0,
    Committed: 1,
} as const;

export type RenditionUploadState = (typeof RenditionUploadState)[keyof typeof RenditionUploadState];

export interface CreativeRendition {
    id: string;
    creativeId: string;
    mediaType: MediaType;
    blobKey: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
    contentHash: string;
    width?: number | null;
    height?: number | null;
    // Advisory only — the device advances on its player's end-of-media signal, not on this. Used here for the
    // loop-length estimate.
    durationSeconds?: number | null;
    uploadState: RenditionUploadState;
}

export interface Creative {
    id: string;
    brandId: string;
    name: string;

    // Attribution. A null advertiser is the common case — the brand's own content, sold to nobody. It lives
    // on the creative rather than a file because an advertiser buys a creative, not a JPEG.
    advertiserId?: string | null;
    advertiserAssetCode?: string | null;
    // Ids only; names come from the tag list rather than being repeated on every creative.
    tagIds: string[];

    // Committed only. A pending rendition has a row before its bytes exist.
    renditions: CreativeRendition[];

    // How many playlists use it. Explains why a delete is refused.
    usageCount: number;

    created: string;
    createdBy: string;
    updated?: string | null;
    updatedBy?: string | null;
}

export interface CreateCreative {
    brandId: string;
    name: string;
}

export interface SetAttribution {
    advertiserId?: string | null;
    advertiserAssetCode?: string | null;
    // The COMPLETE set. An empty array clears every tag.
    tagIds: string[];
}

// ----- Upload handshake -----

export interface CreateRenditionIntent {
    fileName: string;
    contentType: string;
    sizeBytes: number;
}

export interface RenditionUploadIntent {
    renditionId: string;
    blobKey: string;
    // Points at Azure Blob Storage, NOT at our API. The browser PUTs the file here directly.
    uploadUri: string;
    expiresOn: string;
}

export interface CommitRendition {
    // Required for images; the browser reads them off the file. Optional for video, where metadata can fail
    // to load and refusing the upload would be the worse outcome.
    width?: number | null;
    height?: number | null;
    durationSeconds?: number | null;
}

export interface RenditionReadUri {
    readUri: string;
    expiresOn: string;
}

// ----- Display profiles -----

// A shape a creative may be produced at. Platform reference data — the portal reads it, nobody edits it here.
export interface DisplayProfile {
    id: string;
    key: string;
    name: string;
    widthRatio: number;
    heightRatio: number;
    // "10:16", formatted by the backend so every surface says the same thing.
    display: string;
    minWidth?: number | null;
    minHeight?: number | null;
}

export interface AppDisplayProfiles {
    // The same AppType vocabulary a playlist's `apps` uses, so the two can be compared directly.
    appType: AppType;
    profiles: DisplayProfile[];
    // False = platform defaults; nobody chose them.
    isBrandOverride: boolean;
}

// What comes back from adding a creative to a playlist: the shapes it cannot fill.
//
// NOT an error. The creative was added and is simply skipped on those surfaces — refusing would trade a small
// authoring gap for a blank screen. Show it as a warning.
export interface AddPlaylistItemResult {
    missingProfiles: DisplayProfile[];
}

// Which rendition fits a given shape. Used for the library's thumbnail and the coverage badges; the device
// does its own version of this against its actual screen.
export const renditionForProfile = (
    creative: Creative,
    profile: DisplayProfile,
    tolerance = 0.01,
): CreativeRendition | undefined =>
    creative.renditions.find((r) => {
        if (!r.width || !r.height) return false;
        const expected = profile.widthRatio / profile.heightRatio;
        return Math.abs(r.width / r.height - expected) <= expected * tolerance;
    });
